import React, { useCallback, useMemo } from 'react'

import { FieldErrors, UseFormTrigger, UseFormWatch } from 'react-hook-form'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'

import { TableSortType } from 'src/components/molecules'
import { ExternalAssignUserEditTableRow } from 'src/components/molecules/projects/ExternalAssignUserEditTableRow'
import { ExternalAssignUserSortItemType } from 'src/components/molecules/projects/ExternalAssignUserTableHead'

import { useGetExternalUsers } from 'src/hooks'
import { ExternalEditor, ProjectAssignUserEntity, UpsertProjectFormEntity } from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'

type Props = {
  compassRoles: CompassRoleEntity[]
  alreadyAssignedExternalUsers: ProjectAssignUserEntity[]
  displayedAssignedExternalUsers: ExternalEditor[]
  watch: UseFormWatch<UpsertProjectFormEntity>
  setValue: UseFormSetValue<UpsertProjectFormEntity>
  formErrors: FieldErrors<UpsertProjectFormEntity>
  trigger: UseFormTrigger<UpsertProjectFormEntity>
  sortItem: ExternalAssignUserSortItemType
  sortType: TableSortType
  className?: string
}

export function ExternalAssignUserEditTableBody({
  compassRoles,
  alreadyAssignedExternalUsers,
  displayedAssignedExternalUsers,
  watch,
  setValue,
  formErrors,
  trigger,
  sortItem,
  sortType,
  className,
}: Props) {
  const { externalUsers } = useGetExternalUsers()

  const getAssignedAt = useCallback(
    (userId: number) => {
      return alreadyAssignedExternalUsers.find(user => user.id === userId)?.assignedAt
    },
    [alreadyAssignedExternalUsers]
  )

  const sortedExternalUsers = useMemo(() => {
    return [...displayedAssignedExternalUsers].sort((a, b) => {
      const order = sortType === 'desc' ? 1 : -1

      const aAssignedAt = getAssignedAt(a.id)
      const bAssignedAt = getAssignedAt(b.id)

      if (!aAssignedAt) {
        return -1
      }

      if (!bAssignedAt) {
        return 1
      }

      if (sortItem === 'name') {
        return (a.name ?? '-') < (b.name ?? '-') ? order : -order
      }
      if (sortItem === 'email') {
        return (a.email ?? '-') < (b.email ?? '-') ? order : -order
      }
      if (sortItem === 'keywords') {
        const aKeywords = externalUsers.find(user => user.id === a.id)?.keywords
        const aTargetKeywords = aKeywords && aKeywords.length > 0 ? aKeywords[0] : '-'
        const bKeywords = externalUsers.find(user => user.id === b.id)?.keywords
        const bTargetKeywords = bKeywords && bKeywords.length > 0 ? bKeywords[0] : '-'
        return aTargetKeywords < bTargetKeywords ? order : -order
      }

      if (sortItem === 'assignedAt') {
        return aAssignedAt < (bAssignedAt ?? new Date()) ? order : -order
      }
      if (sortItem === 'compassRole') {
        return (a.compassRolePriority ?? 0) < (b.compassRolePriority ?? 0) ? order : -order
      }

      return 0
    })
  }, [displayedAssignedExternalUsers, externalUsers, getAssignedAt, sortItem, sortType])

  // userIdをキーにしたエラーメッセージの配列。useMemoを利用するとformErrorsが変更されたときに再計算されないためuseMemoを使わない
  const errorMaps = (() => {
    const externalEditorErrors = formErrors?.externalEditors
    // 配列であることの確認を入れないとtsのエラーが起きる
    if (!Array.isArray(externalEditorErrors)) {
      return []
    }

    // externalEditorsはソート前（watch("externalEditors")）のindex番号順にエラーメッセージが入っている
    return externalEditorErrors
      ?.map((item, index) => {
        if (!watch('externalEditors')[index]) {
          return undefined
        }

        return { userId: watch('externalEditors')[index].id, message: item?.compassRoleId?.message }
      })
      .filter((v): v is { userId: number; message: string } => !!v)
  })()

  return (
    <div className={className}>
      <ul>
        {sortedExternalUsers.map(externalUser => (
          <li
            className="tw-centering tw-border-b tw-border-b-border-secondary last:tw-border-b-0"
            key={externalUser.id}
          >
            <ExternalAssignUserEditTableRow
              userId={externalUser.id}
              compassRoles={compassRoles}
              selectedValue={externalUser.compassRoleId}
              alreadyAssignedExternalUserIds={alreadyAssignedExternalUsers.map(user => user.id)}
              errorMessage={errorMaps.find(error => error.userId === externalUser.id)?.message}
              watch={watch}
              setValue={setValue}
              trigger={trigger}
              assignedAt={getAssignedAt(externalUser.id)}
              className="tw-w-full"
            />
          </li>
        ))}
      </ul>
    </div>
  )
}
